import { Vector3 } from 'three';
import { SweepMesh } from '../geometry/sweep';
import { TrackSupportPrimitive } from '../supports/types';
import { jointBasis, boxPrism } from './joints';
import { PrintSettings, printMmToWorldMetres } from './types';
/** Footers closer than this (m) are treated as belonging to a column endpoint. */
export const FOOTER_MATCH_M = 0.55;
export const HUB_MATCH_M = 0.4;

/** Half-side of the square concrete pad (`radius` on the footer primitive). */
export function footerPadHalfSide(radius: number): number {
  return Math.max(radius, 0.2);
}

/** Square pad thickness in world metres from print settings. */
export function footerThicknessM(settings: PrintSettings): number {
  return Math.max(printMmToWorldMetres(settings.footerThicknessMm), 0.12);
}

/** Stamp print-config pad thickness and absolute plan size onto every footer. */
export function applyFooterThickness(
  prims: TrackSupportPrimitive[],
  settings: PrintSettings,
): TrackSupportPrimitive[] {
  const height = footerThicknessM(settings);
  const halfSide = Math.max(printMmToWorldMetres(settings.footerSizeMm) * 0.5, 0.05);
  return prims.map((p) =>
    p.kind === 'footer' ? { ...p, height, radius: halfSide } : p,
  );
}

/** Vertical stub length above the pad in world metres. */
export function footerAttachStubM(settings: PrintSettings): number {
  return printMmToWorldMetres(Math.max(settings.footerAttachStubMm, 0));
}

/** Pad size/thickness plus a short vertical HSS stub on each column landing. */
export function applyFooterPrintGeom(
  prims: TrackSupportPrimitive[],
  settings: PrintSettings,
): TrackSupportPrimitive[] {
  return applyFooterAttachStubs(applyFooterThickness(prims, settings), settings);
}

/** Max straight-beam length in world metres (`Infinity` when the setting is 0). */
export function maxBeamLengthM(settings: PrintSettings): number {
  if (!(settings.maxBeamLengthMm > 0)) return Infinity;
  return printMmToWorldMetres(settings.maxBeamLengthMm);
}

/** How many equal segments a beam of `length` needs to stay ≤ `maxLen`. */
export function equalBeamSegmentCount(length: number, maxLen: number): number {
  if (!(maxLen > 0) || !Number.isFinite(maxLen) || length <= maxLen + 1e-6) return 1;
  return Math.max(2, Math.ceil(length / maxLen - 1e-9));
}

/** Mid-beam splice where two HSS bolted flanges meet. */
export interface BeamSplice {
  pos: Vector3;
  /** Unit direction along the original beam (a → b). */
  dir: Vector3;
  radius: number;
}

/**
 * Cut straight columns / braces / knees into equal segments no longer than
 * {@link PrintSettings.maxBeamLengthMm}. Returns the shortened primitives plus
 * splice stations for live HSS flange preview. Stubs are left whole (short taper).
 */
export function applyMaxBeamLengthSplits(
  prims: TrackSupportPrimitive[],
  settings: PrintSettings,
): { prims: TrackSupportPrimitive[]; splices: BeamSplice[] } {
  const maxLen = maxBeamLengthM(settings);
  if (!Number.isFinite(maxLen)) return { prims, splices: [] };

  const out: TrackSupportPrimitive[] = [];
  const splices: BeamSplice[] = [];

  for (const p of prims) {
    if (p.kind !== 'column' && p.kind !== 'brace' && p.kind !== 'knee') {
      out.push(p);
      continue;
    }
    const len = p.a.distanceTo(p.b);
    const n = equalBeamSegmentCount(len, maxLen);
    if (n <= 1) {
      out.push(p);
      continue;
    }
    const dir = p.b.clone().sub(p.a).multiplyScalar(1 / Math.max(len, 1e-9));
    for (let i = 0; i < n; i++) {
      const t0 = i / n;
      const t1 = (i + 1) / n;
      const a = p.a.clone().lerp(p.b, t0);
      const b = p.a.clone().lerp(p.b, t1);
      if (p.kind === 'column') {
        out.push({ kind: 'column', a, b, radius: p.radius, strategy: p.strategy });
      } else if (p.kind === 'brace') {
        out.push({ kind: 'brace', a, b, radius: p.radius });
      } else {
        out.push({ kind: 'knee', a, b, radius: p.radius });
      }
      if (i < n - 1) {
        splices.push({ pos: b.clone(), dir: dir.clone(), radius: p.radius });
      }
    }
  }

  return { prims: out, splices };
}

/**
 * Insert a vertical tube from pad top up `footerAttachStubMm`, and park the
 * column on the stub. Zero length leaves solver geometry unchanged.
 */
export function applyFooterAttachStubs(
  prims: TrackSupportPrimitive[],
  settings: PrintSettings,
): TrackSupportPrimitive[] {
  const stubLen = footerAttachStubM(settings);
  if (stubLen < 1e-4) return prims;

  const footers = prims.filter(
    (p): p is Extract<TrackSupportPrimitive, { kind: 'footer' }> => p.kind === 'footer',
  );
  if (footers.length === 0) return prims;

  const extras: TrackSupportPrimitive[] = [];
  const stubKeys = new Set<string>();

  const next = prims.map((prim) => {
    switch (prim.kind) {
      case 'footer':
      case 'joint':
        return prim;
      case 'column':
      case 'brace':
      case 'knee':
      case 'stub': {
        const radius = prim.kind === 'stub' ? Math.max(prim.radiusA, prim.radiusB) : prim.radius;
        const a = prim.a.clone();
        const b = prim.b.clone();
        raiseMemberForFooterStub(a, b, radius, footers, stubLen, extras, stubKeys);
        return { ...prim, a, b };
      }
      default: {
        const _never: never = prim;
        return _never;
      }
    }
  });

  return [...next, ...extras];
}

function raiseMemberForFooterStub(
  a: Vector3,
  b: Vector3,
  radius: number,
  footers: Extract<TrackSupportPrimitive, { kind: 'footer' }>[],
  stubLen: number,
  extras: TrackSupportPrimitive[],
  stubKeys: Set<string>,
): void {
  for (const which of [0, 1] as const) {
    const end = which === 0 ? a : b;
    const other = which === 0 ? b : a;
    const foot = closestFooterForMemberEnd(end, radius, footers);
    if (!foot) continue;
    const padTopY = foot.pos.y + Math.max(foot.height, 0.12);
    const atPad = new Vector3(foot.pos.x, padTopY, foot.pos.z);
    const stubTop = new Vector3(foot.pos.x, padTopY + stubLen, foot.pos.z);
    if (other.y <= stubTop.y + 0.05) continue;
    if (other.distanceTo(stubTop) < stubLen + 0.08) continue;
    end.copy(stubTop);
    const key = `${foot.pos.x.toFixed(3)},${foot.pos.y.toFixed(3)},${foot.pos.z.toFixed(3)}`;
    if (!stubKeys.has(key)) {
      stubKeys.add(key);
      extras.push({
        kind: 'stub',
        a: atPad,
        b: stubTop.clone(),
        radiusA: radius,
        radiusB: radius,
      });
    }
    const along = other.clone().sub(stubTop);
    if (along.lengthSq() < 1e-12) continue;
    along.normalize();
    if (along.y >= 0.97) continue;
    const jointKey = `j:${key}`;
    if (stubKeys.has(jointKey)) continue;
    stubKeys.add(jointKey);
    extras.push({ kind: 'joint', pos: stubTop.clone(), radius });
  }
}

/** Nearest pad that this member end actually sits on (plan + height). */
function closestFooterForMemberEnd(
  end: Vector3,
  radius: number,
  footers: Extract<TrackSupportPrimitive, { kind: 'footer' }>[],
): Extract<TrackSupportPrimitive, { kind: 'footer' }> | null {
  let best: Extract<TrackSupportPrimitive, { kind: 'footer' }> | null = null;
  let bestDist = Infinity;
  for (const foot of footers) {
    const half = Math.max(footerPadHalfSide(foot.radius), FOOTER_MATCH_M);
    const dx = end.x - foot.pos.x;
    const dz = end.z - foot.pos.z;
    const horiz = Math.max(Math.abs(dx), Math.abs(dz));
    if (horiz > half + Math.max(radius, 0)) continue;
    const padTopY = foot.pos.y + Math.max(foot.height, 0.12);
    if (end.y > padTopY + FOOTER_MATCH_M) continue;
    if (end.y < foot.pos.y - FOOTER_MATCH_M) continue;
    const dist = Math.hypot(dx, dz);
    if (dist < bestDist) {
      bestDist = dist;
      best = foot;
    }
  }
  return best;
}

const CYL_SEGMENTS = 12;

type FooterPad = Pick<Extract<TrackSupportPrimitive, { kind: 'footer' }>, 'pos' | 'radius' | 'height'>;

/**
 * True when a joint sphere sits in/on a ground pad or would bulge below its
 * flat underside. Footers are glue faces — never ball joints.
 */
export function jointOnFooterPad(
  pos: Vector3,
  radius: number,
  footers: FooterPad[],
): boolean {
  const r = Math.max(radius, 0);
  for (const f of footers) {
    const horiz = Math.max(Math.abs(pos.x - f.pos.x), Math.abs(pos.z - f.pos.z));
    const padR = Math.max(footerPadHalfSide(f.radius), FOOTER_MATCH_M);
    if (horiz > padR + r) continue;
    const bottom = f.pos.y;
    const top = f.pos.y + Math.max(f.height, 0.12);
    // Hemisphere (or more) sticking out under the pad.
    if (pos.y - r < bottom - 1e-4 && horiz <= padR) return true;
    // Center in the pad volume, including the glue plane and pad top.
    if (horiz <= padR && pos.y <= top + 1e-3 && pos.y >= bottom - Math.max(r, HUB_MATCH_M)) {
      return true;
    }
  }
  return false;
}

function pointOnFooterPad(pos: Vector3, footers: FooterPad[]): boolean {
  return jointOnFooterPad(pos, 0, footers);
}

/** Ensure every brace/knee endpoint has a joint sphere hub (diameter matches beam). */
export function ensureBeamJointHubs(prims: TrackSupportPrimitive[]): TrackSupportPrimitive[] {
  const footers = prims.filter((p) => p.kind === 'footer');
  // Drop existing balls on/under pads so print slices keep a flat footer underside.
  const stripped = prims.filter(
    (p) => p.kind !== 'joint' || !jointOnFooterPad(p.pos, p.radius, footers),
  );
  const hubs: Vector3[] = [];
  for (const p of stripped) {
    if (p.kind === 'joint') hubs.push(p.pos.clone());
  }
  const out = [...stripped];
  const addHub = (pos: Vector3, radius: number) => {
    if (jointOnFooterPad(pos, radius, footers)) return;
    if (hubs.some((h) => h.distanceTo(pos) < HUB_MATCH_M)) return;
    hubs.push(pos.clone());
    // Sphere diameter matches beam diameter (same diagonal of the tube cross-section).
    out.push({ kind: 'joint', pos: pos.clone(), radius: Math.max(radius, 1e-4) });
  };
  for (const p of stripped) {
    if (p.kind === 'brace' || p.kind === 'knee') {
      addHub(p.a, p.radius);
      addHub(p.b, p.radius);
    }
  }
  return dedupeJointHubs(out);
}

/**
 * Collapse overlapping joint spheres (support gen often emits one per member end).
 * Duplicate solid hubs unioned without a shared cavity fill female sockets shut.
 */
export function dedupeJointHubs(prims: TrackSupportPrimitive[]): TrackSupportPrimitive[] {
  const joints: Extract<TrackSupportPrimitive, { kind: 'joint' }>[] = [];
  const rest: TrackSupportPrimitive[] = [];
  for (const p of prims) {
    if (p.kind === 'joint') joints.push(p);
    else rest.push(p);
  }
  const kept: Extract<TrackSupportPrimitive, { kind: 'joint' }>[] = [];
  for (const j of joints) {
    const existing = kept.find((k) => k.pos.distanceTo(j.pos) < HUB_MATCH_M);
    if (!existing) {
      kept.push({ kind: 'joint', pos: j.pos.clone(), radius: j.radius });
    } else {
      existing.radius = Math.max(existing.radius, j.radius);
    }
  }
  return [...rest, ...kept];
}

/**
 * Clip columns/braces so they sit on top of footers (flat glue face), never under them.
 */
export function prepareSupportPrimitives(
  prims: TrackSupportPrimitive[],
): TrackSupportPrimitive[] {
  const footers = prims.filter((p) => p.kind === 'footer') as Extract<
    TrackSupportPrimitive,
    { kind: 'footer' }
  >[];

  return prims.map((prim) => {
    if (prim.kind === 'footer' || prim.kind === 'joint') return prim;
    const radius =
      prim.kind === 'stub' ? Math.max(prim.radiusA, prim.radiusB) : prim.radius;
    return {
      ...prim,
      a: clipAwayFromFooters(prim.a, prim.b, footers, radius),
      b: clipAwayFromFooters(prim.b, prim.a, footers, radius),
    };
  });
}

/** If `point` sits in/under a footer pad, raise it to the pad top along the member axis. */
function clipAwayFromFooters(
  point: Vector3,
  other: Vector3,
  footers: Extract<TrackSupportPrimitive, { kind: 'footer' }>[],
  radius: number,
): Vector3 {
  let out = point.clone();
  for (const f of footers) {
    const dx = point.x - f.pos.x;
    const dz = point.z - f.pos.z;
    const horiz = Math.max(Math.abs(dx), Math.abs(dz));
    if (horiz > Math.max(footerPadHalfSide(f.radius), FOOTER_MATCH_M) + Math.max(radius, 0)) continue;
    const topY = f.pos.y + Math.max(f.height, 0.12);
    if (out.y >= topY - 1e-6) continue;
    out = raiseToPlaneY(out, other, topY);
  }
  return out;
}

/** True when a member endpoint sits on a footer pad top (glue face). */
export function endOnFooterPad(pos: Vector3, footers: FooterPad[]): boolean {
  return pointOnFooterPad(pos, footers);
}

function raiseToPlaneY(point: Vector3, other: Vector3, planeY: number): Vector3 {
  if (point.y >= planeY - 1e-9) return point.clone();
  const dy = other.y - point.y;
  if (Math.abs(dy) < 1e-9) return new Vector3(point.x, planeY, point.z);
  const t = (planeY - point.y) / dy;
  return point.clone().lerp(other, Math.min(1, Math.max(0, t)));
}

export interface CylinderMeshOpts {
  /** Slice end A with a horizontal plane at `a.y` (footer glue face). */
  horizontalCapA?: boolean;
  /** Slice end B with a horizontal plane at `b.y` (footer glue face). */
  horizontalCapB?: boolean;
  /** Ring tessellation (default 12). */
  segments?: number;
}

/** Flat-bottom square concrete pad — underside is planar for gluing / placing. */
export function flatFooterMesh(pos: Vector3, radius: number, height: number): SweepMesh {
  const h = Math.max(height, 0.12);
  const half = footerPadHalfSide(radius);
  const bottom = new Vector3(pos.x, pos.y, pos.z);
  const top = new Vector3(pos.x, pos.y + h, pos.z);
  const basis = jointBasis(bottom, new Vector3(0, 1, 0), new Vector3(1, 0, 0), new Vector3(0, 0, 1));
  return boxPrism(bottom, top, basis, half, half);
}

export function cylinderMesh(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  opts?: CylinderMeshOpts,
): SweepMesh {
  const dir = new Vector3().subVectors(b, a);
  const len = Math.max(dir.length(), 1e-6);
  dir.multiplyScalar(1 / len);

  const up = Math.abs(dir.y) < 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
  const right = new Vector3().crossVectors(dir, up).normalize();
  const forward = new Vector3().crossVectors(right, dir).normalize();

  const rings = 2;
  const seg = Math.max(6, opts?.segments ?? CYL_SEGMENTS);
  const positions = new Float32Array((rings * seg + 2) * 3);
  const normals = new Float32Array(positions.length);
  const indices: number[] = [];
  const horizontal = [opts?.horizontalCapA === true, opts?.horizontalCapB === true];

  for (let r = 0; r < rings; r++) {
    const radius = r === 0 ? radiusA : radiusB;
    const center = new Vector3().copy(a).addScaledVector(dir, r * len);
    for (let i = 0; i < seg; i++) {
      const ang = (i / seg) * Math.PI * 2;
      const nx = Math.cos(ang);
      const nz = Math.sin(ang);
      const offset = new Vector3()
        .copy(right)
        .multiplyScalar(nx * radius)
        .addScaledVector(forward, nz * radius);
      const p = ringVertex(center, offset, dir, horizontal[r]);
      const vi = (r * seg + i) * 3;
      positions[vi] = p.x;
      positions[vi + 1] = p.y;
      positions[vi + 2] = p.z;
      const nn = new Vector3()
        .copy(right)
        .multiplyScalar(nx)
        .addScaledVector(forward, nz)
        .normalize();
      normals[vi] = nn.x;
      normals[vi + 1] = nn.y;
      normals[vi + 2] = nn.z;
    }
  }

  for (let i = 0; i < seg; i++) {
    const i1 = (i + 1) % seg;
    indices.push(i, seg + i, i1, i1, seg + i, seg + i1);
  }

  const bot = rings * seg;
  const top = bot + 1;
  positions[bot * 3] = a.x;
  positions[bot * 3 + 1] = a.y;
  positions[bot * 3 + 2] = a.z;
  const aN = horizontal[0] ? new Vector3(0, a.y <= b.y ? -1 : 1, 0) : dir.clone().multiplyScalar(-1);
  normals[bot * 3] = aN.x;
  normals[bot * 3 + 1] = aN.y;
  normals[bot * 3 + 2] = aN.z;
  positions[top * 3] = b.x;
  positions[top * 3 + 1] = b.y;
  positions[top * 3 + 2] = b.z;
  const bN = horizontal[1] ? new Vector3(0, b.y <= a.y ? -1 : 1, 0) : dir.clone();
  normals[top * 3] = bN.x;
  normals[top * 3 + 1] = bN.y;
  normals[top * 3 + 2] = bN.z;

  for (let i = 0; i < seg; i++) {
    const i1 = (i + 1) % seg;
    indices.push(bot, i1, i);
    indices.push(top, seg + i, seg + i1);
  }

  return { positions, normals, indices: Uint32Array.from(indices) };
}

/**
 * Perp-tube ring vertex, optionally slid along the axis onto the horizontal
 * plane through `center` so a leaning beam ends flush on a footer pad.
 */
function ringVertex(
  center: Vector3,
  offset: Vector3,
  dir: Vector3,
  horizontal: boolean,
): Vector3 {
  const p = center.clone().add(offset);
  if (!horizontal) return p;
  if (Math.abs(dir.y) < 0.08) {
    p.y = Math.max(p.y, center.y);
    return p;
  }
  const t = (center.y - p.y) / dir.y;
  return p.addScaledVector(dir, t);
}

export function meshPoints(mesh: SweepMesh): Vector3[] {
  const pts: Vector3[] = [];
  for (let i = 0; i < mesh.positions.length; i += 3) {
    pts.push(new Vector3(mesh.positions[i], mesh.positions[i + 1], mesh.positions[i + 2]));
  }
  return pts;
}
